import { Button } from '@repo/design-system/components/ds/button';
import { Icon } from '@repo/design-system/components/ds/icon';
import { FlexColumn } from '@repo/design-system/components/layout/flex-column';
import { Select, SelectItem } from '@repo/design-system/components/ui/select';
import { toast } from '@repo/design-system/components/ui/toaster';
import { useCurrentLanguage } from '@repo/i18n/hooks/use-current-language';
import { useGetAIPromptConfigQuery } from '@repo/store/maps/api/mapsApi';
import { useAppDispatch, useAppSelector } from '@repo/store/maps/hooks';
import { setActiveHeroActionId, setHeroActionTriggerPinId, setPreviewHeroAction, setSidebarOpen, setSidebarView } from '@repo/store/maps/slices/clientContext';
import { selectCurrentMapInfo, selectExternalMapId, selectHeroActions, selectMapData, updateMapData } from '@repo/store/maps/slices/mapData';
import type { MapData, MapDataHeroAction, MapDataHeroActionIconCategory } from '@repo/types/maps/map-types';
import { cancelHeroActionGeneration, createHeroAction, getGameMap, getHeroActionGeneration, type HeroActionGeneration, type HeroActionPreviewResponse, listHeroActions, startHeroActionGeneration, updateHeroAction } from '@repo/wemod-api-client/maps-api-client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMapPinOperations } from '../../game-map/hooks/useMapPinOperations';
import { getAuthTokenFromCookie } from '../../utils/auth-cookie';
import { EMPTY_MANUAL_EDITOR_STATE, HERO_ACTION_GENERATION_POLL_GRACE_MS, HERO_ACTION_GENERATION_POLL_MS, HERO_ACTION_PROMPT_KEY, NEW_HERO_ACTION_EXTERNAL_ID } from './hero-actions/constants';
import { HeroActionEditorModal } from './hero-actions/hero-action-editor-modal';
import type { HeroActionManualEditorState } from './hero-actions/types';
import { areManualEditorStatesEqual, buildHeroActionPinDetails, buildMapPinDetails, buildPreviewPinDetails, delay, formatPinIds, getCurrentMapResultPinIds, getHeroActionMapExternalIds, getManualEditorState, groupManualPinIdsForPreview, isTerminalGenerationState, mapSavedHeroActionToMapDataHeroAction, parsePinExternalIds, toHeroActionIconCategory } from './hero-actions/utils';
export const HeroActions = () => {
  const dispatch = useAppDispatch();
  const currentLanguage = useCurrentLanguage();
  const mapInfo = useAppSelector(selectCurrentMapInfo);
  const mapExternalId = useAppSelector(selectExternalMapId);
  const mapData = useAppSelector(selectMapData);
  const heroActions = useAppSelector(selectHeroActions);
  const {
    clearSelectedMarkerInMap
  } = useMapPinOperations();
  const {
    data: promptConfig
  } = useGetAIPromptConfigQuery({
    key: HERO_ACTION_PROMPT_KEY
  }, {
    refetchOnMountOrArgChange: 300
  });
  const [description, setDescription] = useState('');
  const [prompt, setPrompt] = useState('');
  const [lockToCurrentMap, setLockToCurrentMap] = useState(true);
  const [enableWebSearch, setEnableWebSearch] = useState(false);
  const [keepCurrentPins, setKeepCurrentPins] = useState(false);
  const [preview, setPreview] = useState<HeroActionPreviewResponse | null>(null);
  const [generationStatus, setGenerationStatus] = useState<HeroActionGeneration | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [selectedHeroActionId, setSelectedHeroActionId] = useState('');
  const [manualName, setManualName] = useState('');
  const [manualDescription, setManualDescription] = useState('');
  const [manualIconCategory, setManualIconCategory] = useState<MapDataHeroActionIconCategory>('loot_gear');
  const [manualPriority, setManualPriority] = useState('0');
  const [manualResultPins, setManualResultPins] = useState('');
  const [manualTriggerPins, setManualTriggerPins] = useState('');
  const [isManualSaving, setIsManualSaving] = useState(false);
  const [isManualEditorOpen, setIsManualEditorOpen] = useState(false);
  const [isLoadingEditorMaps, setIsLoadingEditorMaps] = useState(false);
  const [editorMapDataByExternalId, setEditorMapDataByExternalId] = useState<Record<string, MapData>>({});
  const [managedHeroActions, setManagedHeroActions] = useState<ReadonlyArray<MapDataHeroAction>>([]);
  const hasHydratedPromptConfig = useRef(false);
  const previewHeroActionSequenceRef = useRef(0);
  const activeGenerationIdRef = useRef<string | null>(null);
  const cancelGenerationRequestedRef = useRef(false);
  const editorMapDataByExternalIdRef = useRef<Record<string, MapData>>({});
  useEffect(() => {
    return () => {
      activeGenerationIdRef.current = null;
      cancelGenerationRequestedRef.current = false;
    };
  }, []);
  useEffect(() => {
    if (!promptConfig || hasHydratedPromptConfig.current) {
      return;
    }
    hasHydratedPromptConfig.current = true;
    if (promptConfig.instructions) {
      setPrompt(promptConfig.instructions);
    }
  }, [promptConfig]);
  const requestScope = useMemo(() => ({
    titleId: mapInfo?.titleId ? String(mapInfo.titleId) : undefined,
    gameMapExternalId: mapExternalId ?? undefined,
    lockToCurrentMap
  }), [lockToCurrentMap, mapExternalId, mapInfo?.titleId]);
  const selectedHeroAction = useMemo(() => (managedHeroActions.length > 0 ? managedHeroActions : heroActions).find(action => action.externalId === selectedHeroActionId) ?? null, [heroActions, managedHeroActions, selectedHeroActionId]);
  const editableHeroActions = managedHeroActions.length > 0 ? managedHeroActions : heroActions;
  const selectedActionCurrentMapResultPins = useMemo(() => getCurrentMapResultPinIds(selectedHeroAction, mapExternalId), [mapExternalId, selectedHeroAction]);
  const currentMapPinDetails = useMemo(() => buildMapPinDetails(mapData, mapExternalId ?? undefined, mapExternalId ?? undefined), [mapData, mapExternalId]);
  const selectedActionPinDetails = useMemo(() => buildHeroActionPinDetails(selectedHeroAction, mapExternalId ?? undefined), [mapExternalId, selectedHeroAction]);
  const generatedPreviewPinDetails = useMemo(() => buildPreviewPinDetails(preview), [preview]);
  const editorPinDetails = useMemo(() => {
    const details = new Map(selectedActionPinDetails);
    for (const [pinId, pinDetails] of generatedPreviewPinDetails) {
      details.set(pinId, pinDetails);
    }
    for (const [pinId_0, pinDetails_0] of currentMapPinDetails) {
      details.set(pinId_0, pinDetails_0);
    }
    for (const [relatedMapExternalId, relatedMapData] of Object.entries(editorMapDataByExternalId)) {
      for (const [pinId_1, pinDetails_1] of buildMapPinDetails(relatedMapData, relatedMapExternalId, mapExternalId ?? undefined)) {
        details.set(pinId_1, pinDetails_1);
      }
    }
    return details;
  }, [currentMapPinDetails, editorMapDataByExternalId, generatedPreviewPinDetails, mapExternalId, selectedActionPinDetails]);
  const editorPinMapById = useMemo(() => {
    const maps = new Map<string, string>();
    for (const [pinId_2, details_0] of editorPinDetails) {
      if (details_0.mapExternalId) {
        maps.set(pinId_2, details_0.mapExternalId);
      }
    }
    return maps;
  }, [editorPinDetails]);
  const getEditorPinDetails = useCallback((pinId_3: string) => editorPinDetails.get(pinId_3), [editorPinDetails]);
  const manualCurrentMapResultPins = useMemo(() => parsePinExternalIds(manualResultPins).filter(pinId_4 => currentMapPinDetails.has(pinId_4)), [currentMapPinDetails, manualResultPins]);
  const currentMapPinOptions = useMemo(() => Array.from(currentMapPinDetails.entries()).map(([externalId, details_1]) => ({
    externalId,
    ...details_1
  })).sort((a, b) => a.title.localeCompare(b.title)), [currentMapPinDetails]);
  const originalManualEditorState = useMemo(() => selectedHeroAction ? getManualEditorState(selectedHeroAction) : EMPTY_MANUAL_EDITOR_STATE, [selectedHeroAction]);
  const currentManualEditorState = useMemo<HeroActionManualEditorState>(() => ({
    name: manualName,
    description: manualDescription,
    iconCategory: manualIconCategory,
    priority: manualPriority,
    resultPins: manualResultPins,
    triggerPins: manualTriggerPins
  }), [manualDescription, manualIconCategory, manualName, manualPriority, manualResultPins, manualTriggerPins]);
  const hasUnsavedManualEditorChanges = useMemo(() => !areManualEditorStatesEqual(currentManualEditorState, originalManualEditorState), [currentManualEditorState, originalManualEditorState]);
  const canOpenManualEditor = selectedHeroAction !== null || hasUnsavedManualEditorChanges;
  const openManualEditorLabel = hasUnsavedManualEditorChanges ? 'Resume Editing' : 'Edit Selected';
  const applyManualEditorState = useCallback((state: HeroActionManualEditorState) => {
    setManualName(state.name);
    setManualDescription(state.description);
    setManualIconCategory(state.iconCategory);
    setManualPriority(state.priority);
    setManualResultPins(state.resultPins);
    setManualTriggerPins(state.triggerPins);
  }, []);
  const resetManualEditorState = useCallback(() => {
    applyManualEditorState(originalManualEditorState);
  }, [applyManualEditorState, originalManualEditorState]);
  const editingHeroAction = useMemo<MapDataHeroAction>(() => selectedHeroAction ?? {
    externalId: NEW_HERO_ACTION_EXTERNAL_ID,
    name: manualName || 'New Hero Action',
    description: manualDescription,
    iconCategory: manualIconCategory,
    priority: Number.parseInt(manualPriority, 10) || 0,
    isApproved: false,
    resultPins: {},
    triggerPins: {}
  }, [manualDescription, manualIconCategory, manualName, manualPriority, selectedHeroAction]);
  const showHeroActionOnMap = useCallback((heroActionId: string) => {
    clearSelectedMarkerInMap();
    dispatch(setActiveHeroActionId(heroActionId));
    dispatch(setHeroActionTriggerPinId(null));
    dispatch(setSidebarOpen(true));
    dispatch(setSidebarView('heroAction'));
  }, [clearSelectedMarkerInMap, dispatch]);
  const handleOpenManualEditor = useCallback(() => {
    setIsManualEditorOpen(true);
  }, []);
  useEffect(() => {
    if (!selectedHeroAction) {
      applyManualEditorState(EMPTY_MANUAL_EDITOR_STATE);
      return;
    }
    resetManualEditorState();
  }, [applyManualEditorState, resetManualEditorState, selectedHeroAction]);
  const refreshCurrentMapData = useCallback(async () => {
    if (!mapExternalId) {
      return;
    }
    const mapData_0 = await getGameMap(mapExternalId, currentLanguage, {
      debug: true,
      token: getAuthTokenFromCookie()
    });
    dispatch(updateMapData(mapData_0));
  }, [currentLanguage, dispatch, mapExternalId]);
  const refreshHeroActions = useCallback(async () => {
    if (!requestScope.titleId && !requestScope.gameMapExternalId) {
      return;
    }
    const response = await listHeroActions({
      titleId: requestScope.titleId,
      gameMapExternalId: requestScope.gameMapExternalId
    });
    setManagedHeroActions(response.heroActions);
  }, [requestScope.gameMapExternalId, requestScope.titleId]);
  const refreshHeroActionData = useCallback(async () => {
    await Promise.allSettled([refreshCurrentMapData(), refreshHeroActions()]);
  }, [refreshCurrentMapData, refreshHeroActions]);
  useEffect(() => {
    refreshHeroActions().catch(() => {
      setManagedHeroActions([]);
    });
  }, [refreshHeroActions]);
  useEffect(() => {
    if (!isManualEditorOpen || !selectedHeroAction) {
      return;
    }
    const relatedMapExternalIds = getHeroActionMapExternalIds(selectedHeroAction).filter(relatedMapExternalId_0 => relatedMapExternalId_0 !== mapExternalId && !editorMapDataByExternalIdRef.current[relatedMapExternalId_0]);
    if (relatedMapExternalIds.length === 0) {
      return;
    }
    let isCancelled = false;
    setIsLoadingEditorMaps(true);
    void Promise.allSettled(relatedMapExternalIds.map(relatedMapExternalId_1 => getGameMap(relatedMapExternalId_1, currentLanguage, {
      debug: true,
      token: getAuthTokenFromCookie()
    }))).then(results => {
      if (isCancelled) return;
      const loadedMaps: Record<string, MapData> = {};
      results.forEach((result, index) => {
        const relatedMapExternalId_2 = relatedMapExternalIds[index];
        if (!relatedMapExternalId_2) return;
        if (result.status === 'fulfilled') {
          loadedMaps[relatedMapExternalId_2] = result.value;
        }
      });
      if (Object.keys(loadedMaps).length > 0) {
        setEditorMapDataByExternalId(previous => {
          const next = {
            ...previous,
            ...loadedMaps
          };
          editorMapDataByExternalIdRef.current = next;
          return next;
        });
      }
    }).finally(() => {
      if (!isCancelled) {
        setIsLoadingEditorMaps(false);
      }
    });
    return () => {
      isCancelled = true;
    };
  }, [currentLanguage, isManualEditorOpen, mapExternalId, selectedHeroAction]);
  const applyGeneratedPreviewToEditor = useCallback((response_0: HeroActionPreviewResponse, pinsOnly = false) => {
    const normalized = response_0.preview.normalized;
    if (!response_0.preview.valid || !normalized) {
      return;
    }
    if (!pinsOnly) {
      setManualName(normalized.name);
      setManualDescription(normalized.description ?? '');
      setManualIconCategory(toHeroActionIconCategory(normalized.iconCategory));
      setManualPriority(String(normalized.priority));
    }
    setManualResultPins(formatPinIds(response_0.preview.pins.result.map(pin => pin.externalId)));
    setManualTriggerPins(formatPinIds(response_0.preview.pins.trigger.map(pin_0 => pin_0.externalId)));
  }, []);
  const handleCancelGeneration = useCallback(async () => {
    const generationId = activeGenerationIdRef.current ?? generationStatus?.id;
    cancelGenerationRequestedRef.current = true;
    setIsGenerating(false);
    if (!generationId) {
      toast({
        title: 'Hero action generation cancel requested',
        description: 'The generation will be canceled as soon as it starts.',
        variant: 'info'
      });
      return;
    }
    activeGenerationIdRef.current = null;
    try {
      const response_1 = await cancelHeroActionGeneration(generationId);
      setGenerationStatus(response_1.generation);
      toast({
        title: 'Hero action generation canceled',
        variant: 'success'
      });
    } catch (error) {
      toast({
        title: 'Failed to cancel hero action generation',
        description: error instanceof Error ? error.message : undefined,
        variant: 'error'
      });
    }
  }, [generationStatus?.id]);
  const handleGenerate = useCallback(async () => {
    if (!requestScope.titleId && !requestScope.gameMapExternalId) {
      toast({
        title: 'Map context unavailable',
        variant: 'error'
      });
      return;
    }
    setIsGenerating(true);
    cancelGenerationRequestedRef.current = false;
    setPreview(null);
    setGenerationStatus(null);
    dispatch(setPreviewHeroAction(null));
    let generationId_0: string | null = null;
    try {
      const isRegenerating = selectedHeroAction !== null;
      const startResponse = await startHeroActionGeneration({
        ...requestScope,
        lockToCurrentMap: isRegenerating ? false : requestScope.lockToCurrentMap,
        heroActionExternalId: selectedHeroAction?.externalId,
        keepCurrentPins: isRegenerating ? keepCurrentPins : undefined,
        description: description.trim() || undefined,
        prompt: prompt.trim() || undefined,
        maxTokens: promptConfig?.maxTokens,
        maxSteps: promptConfig?.maxSteps ?? undefined,
        timeoutSeconds: promptConfig?.timeoutSeconds ?? undefined,
        temperature: promptConfig?.temperature ?? undefined,
        enableWebSearch,
        webSearchMaxUses: enableWebSearch ? 5 : undefined
      });
      generationId_0 = startResponse.generation.id;
      if (cancelGenerationRequestedRef.current) {
        try {
          const cancelResponse = await cancelHeroActionGeneration(generationId_0);
          setGenerationStatus(cancelResponse.generation);
          toast({
            title: 'Hero action generation canceled',
            variant: 'success'
          });
        } catch (error_1) {
          setGenerationStatus(startResponse.generation);
          toast({
            title: 'Failed to cancel hero action generation',
            description: error_1 instanceof Error ? error_1.message : undefined,
            variant: 'error'
          });
        }
        return;
      }
      activeGenerationIdRef.current = generationId_0;
      setGenerationStatus(startResponse.generation);
      const generationExpiresAtMs = Date.parse(startResponse.generation.expiresAt);
      const pollDeadlineMs = Number.isNaN(generationExpiresAtMs) ? Date.now() + (promptConfig?.timeoutSeconds ?? 120) * 1000 + HERO_ACTION_GENERATION_POLL_GRACE_MS : generationExpiresAtMs + HERO_ACTION_GENERATION_POLL_GRACE_MS;
      let generation = startResponse.generation;
      while (!isTerminalGenerationState(generation.state)) {
        const delayMs = Math.min(HERO_ACTION_GENERATION_POLL_MS, Math.max(0, pollDeadlineMs - Date.now()));
        await delay(delayMs);
        if (activeGenerationIdRef.current !== generationId_0) {
          return;
        }
        if (Date.now() >= pollDeadlineMs) {
          const cancelResponse_0 = await cancelHeroActionGeneration(generationId_0);
          setGenerationStatus(cancelResponse_0.generation);
          toast({
            title: 'Hero action generation timed out',
            description: 'Stopped polling and canceled the generation job.',
            variant: 'error'
          });
          return;
        }
        generation = (await getHeroActionGeneration(generationId_0)).generation;
        if (activeGenerationIdRef.current !== generationId_0) {
          return;
        }
        setGenerationStatus(generation);
      }
      if (generation.state === 'completed' && generation.result) {
        setPreview(generation.result);
        applyGeneratedPreviewToEditor(generation.result, isRegenerating);
        toast({
          title: isRegenerating ? 'Hero action pins regenerated' : 'Hero action populated',
          variant: 'success'
        });
        return;
      }
      toast({
        title: 'Hero action preview failed',
        description: generation.error ?? generation.message ?? undefined,
        variant: 'error'
      });
    } catch (error_0) {
      toast({
        title: 'Hero action preview failed',
        description: error_0 instanceof Error ? error_0.message : undefined,
        variant: 'error'
      });
    } finally {
      if (generationId_0 === null || activeGenerationIdRef.current === generationId_0 || cancelGenerationRequestedRef.current) {
        setIsGenerating(false);
        activeGenerationIdRef.current = null;
        cancelGenerationRequestedRef.current = false;
      }
    }
  }, [applyGeneratedPreviewToEditor, description, dispatch, enableWebSearch, keepCurrentPins, prompt, promptConfig?.maxSteps, promptConfig?.maxTokens, promptConfig?.temperature, promptConfig?.timeoutSeconds, requestScope, selectedHeroAction]);
  const handleViewSelectedHeroAction = useCallback(async () => {
    if (!selectedHeroAction || selectedActionCurrentMapResultPins.length === 0) {
      return;
    }
    try {
      await refreshCurrentMapData();
      dispatch(setPreviewHeroAction(selectedHeroAction));
      showHeroActionOnMap(selectedHeroAction.externalId);
    } catch (error_2) {
      toast({
        title: 'Failed to refresh hero action',
        description: error_2 instanceof Error ? error_2.message : undefined,
        variant: 'error'
      });
    }
  }, [dispatch, refreshCurrentMapData, selectedActionCurrentMapResultPins.length, selectedHeroAction, showHeroActionOnMap]);
  const handlePreviewManualHeroAction = useCallback(async () => {
    if (!mapExternalId) {
      return;
    }
    const resultPinIds = parsePinExternalIds(manualResultPins);
    if (resultPinIds.filter(pinId_5 => currentMapPinDetails.has(pinId_5)).length === 0) {
      toast({
        title: 'No current-map result pins to preview',
        variant: 'error'
      });
      return;
    }
    setIsPreviewing(true);
    try {
      const heroAction: MapDataHeroAction = {
        ...editingHeroAction,
        externalId: `hero-action-editor-preview-${++previewHeroActionSequenceRef.current}`,
        name: manualName.trim() || editingHeroAction.name,
        description: manualDescription.trim(),
        iconCategory: manualIconCategory,
        priority: Number.parseInt(manualPriority, 10) || 0,
        isApproved: false,
        resultPins: groupManualPinIdsForPreview(resultPinIds, editingHeroAction.resultPins, mapExternalId, editorPinMapById),
        triggerPins: groupManualPinIdsForPreview(parsePinExternalIds(manualTriggerPins), editingHeroAction.triggerPins, mapExternalId, editorPinMapById)
      };
      await refreshCurrentMapData();
      dispatch(setPreviewHeroAction(heroAction));
      showHeroActionOnMap(heroAction.externalId);
      setIsManualEditorOpen(false);
    } catch (error_3) {
      toast({
        title: 'Failed to preview hero action changes',
        description: error_3 instanceof Error ? error_3.message : undefined,
        variant: 'error'
      });
    } finally {
      setIsPreviewing(false);
    }
  }, [currentMapPinDetails, dispatch, editingHeroAction, editorPinMapById, manualDescription, manualIconCategory, manualName, manualPriority, manualResultPins, manualTriggerPins, mapExternalId, refreshCurrentMapData, showHeroActionOnMap]);
  const handleManualSave = useCallback(async () => {
    if (!requestScope.titleId && !requestScope.gameMapExternalId) {
      return;
    }
    const suggestion = {
      name: manualName.trim(),
      description: manualDescription.trim() || null,
      icon_category: manualIconCategory,
      priority: Number.parseInt(manualPriority, 10) || 0,
      result_pin_external_ids: parsePinExternalIds(manualResultPins),
      trigger_pin_external_ids: parsePinExternalIds(manualTriggerPins)
    };
    setIsManualSaving(true);
    try {
      let savedHeroAction: MapDataHeroAction;
      if (selectedHeroAction) {
        const response_2 = await updateHeroAction(selectedHeroAction.externalId, {
          titleId: requestScope.titleId,
          gameMapExternalId: requestScope.gameMapExternalId,
          lockToCurrentMap: false,
          suggestion
        });
        savedHeroAction = mapSavedHeroActionToMapDataHeroAction(response_2, currentLanguage);
      } else {
        const response_3 = await createHeroAction({
          titleId: requestScope.titleId,
          gameMapExternalId: requestScope.gameMapExternalId,
          lockToCurrentMap: false,
          suggestion
        });
        savedHeroAction = mapSavedHeroActionToMapDataHeroAction(response_3, currentLanguage);
      }
      setPreview(null);
      dispatch(setPreviewHeroAction(savedHeroAction));
      await refreshHeroActionData();
      setSelectedHeroActionId(savedHeroAction.externalId);
      showHeroActionOnMap(savedHeroAction.externalId);
      toast({
        title: selectedHeroAction ? 'Hero action draft updated' : 'Hero action draft saved',
        variant: 'success'
      });
    } catch (error_4) {
      toast({
        title: selectedHeroAction ? 'Failed to update hero action' : 'Failed to save hero action',
        description: error_4 instanceof Error ? error_4.message : undefined,
        variant: 'error'
      });
    } finally {
      setIsManualSaving(false);
    }
  }, [currentLanguage, dispatch, manualDescription, manualIconCategory, manualName, manualPriority, manualResultPins, manualTriggerPins, refreshHeroActionData, requestScope, selectedHeroAction, showHeroActionOnMap]);
  return <FlexColumn gap="10">
            <Button onClick={() => {
      setSelectedHeroActionId('');
      applyManualEditorState(EMPTY_MANUAL_EDITOR_STATE);
      setPreview(null);
      setGenerationStatus(null);
      setLockToCurrentMap(true);
      setKeepCurrentPins(false);
      setIsManualEditorOpen(true);
    }} color="primary" size="sm" fullWidth>
                <Icon icon="add" decorative />
                New Hero Action
            </Button>

            <Select label="Edit Existing" value={selectedHeroActionId} onValueChange={value => {
      setSelectedHeroActionId(value);
      setPreview(null);
      setGenerationStatus(null);
      setLockToCurrentMap(false);
      setKeepCurrentPins(false);
    }} placeholder="Select hero action" size="sm" fullWidth searchable aria-label="Select hero action to edit" trigger={<Button color="secondary" size="sm" fullWidth>
                        {selectedHeroAction?.name ?? 'Select hero action'}
                    </Button>}>
                {editableHeroActions.map(action_0 => <SelectItem key={action_0.externalId} item={{
        label: action_0.name,
        value: action_0.externalId
      }}>
                        {action_0.name}
                        {action_0.isApproved === false ? ' (draft)' : ''}
                    </SelectItem>)}
            </Select>

            {canOpenManualEditor && <Button onClick={handleOpenManualEditor} color="secondary" size="sm" fullWidth disabled={isManualSaving}>
                    <Icon icon="edit" decorative />
                    {openManualEditorLabel}
                </Button>}

            {selectedHeroAction && <Button onClick={handleViewSelectedHeroAction} color="secondary" size="sm" fullWidth disabled={isManualSaving || isGenerating || selectedActionCurrentMapResultPins.length === 0}>
                    <Icon icon="visibility" decorative />
                    View Saved
                </Button>}

            <HeroActionEditorModal isOpen={isManualEditorOpen} heroAction={editingHeroAction} name={manualName} description={manualDescription} iconCategory={manualIconCategory} priority={manualPriority} resultPins={manualResultPins} triggerPins={manualTriggerPins} generationDescription={description} prompt={prompt} lockToCurrentMap={lockToCurrentMap} enableWebSearch={enableWebSearch} keepCurrentPins={keepCurrentPins} generationStatus={generationStatus} isGenerating={isGenerating} isSaving={isManualSaving} isPreviewing={isPreviewing} isLoadingPinDetails={isLoadingEditorMaps} canPreview={manualCurrentMapResultPins.length > 0} hasUnsavedChanges={hasUnsavedManualEditorChanges} currentMapPinOptions={currentMapPinOptions} getPinDetails={getEditorPinDetails} onClose={() => setIsManualEditorOpen(false)} onDiscardChanges={resetManualEditorState} onNameChange={setManualName} onDescriptionChange={setManualDescription} onIconCategoryChange={setManualIconCategory} onPriorityChange={setManualPriority} onResultPinsChange={setManualResultPins} onTriggerPinsChange={setManualTriggerPins} onGenerationDescriptionChange={setDescription} onPromptChange={setPrompt} onLockToCurrentMapChange={setLockToCurrentMap} onEnableWebSearchChange={setEnableWebSearch} onKeepCurrentPinsChange={setKeepCurrentPins} onGenerate={handleGenerate} onCancelGeneration={handleCancelGeneration} onPreview={handlePreviewManualHeroAction} onSave={handleManualSave} />
        </FlexColumn>;
};